// What the Sites screen (mockup 08) shows, as pure functions of the API data: the routes a site
// carries and their approval state, who may reach the hosts behind it under the active policy,
// and the client-side mirror of the "Site routes" rules (CONTRACT 3.4).
import type { Device, PolicyRule, PolicyVersion } from '../../api/types';
import { canonicalCidr, cidrsOverlap, compareCidrs, isNetworkAddress, parseCidr } from '../../lib/cidr';

/** The whole overlay (CONTRACT 1); no site route may overlap it. */
export const OVERLAY_CIDR = '10.88.0.0/16';
export const MAX_ROUTES = 16;

/** Never routable through a site (CONTRACT 3.4 "Site routes"). */
const RESERVED: [string, string][] = [
  ['0.0.0.0/8', 'this-network'],
  ['127.0.0.0/8', 'loopback'],
  ['169.254.0.0/16', 'link-local'],
  ['224.0.0.0/3', 'multicast'],
];

/** The sites among the devices, by name. */
export function sitesOf(devices: Device[]): Device[] {
  return devices.filter((d) => d.kind === 'site').sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Why `route` cannot be a route of site `siteId`, or null when it can. Checks the shape, the
 * overlay, the reserved ranges and every other site's approved routes; the API checks the
 * relay's own subnet as well.
 */
export function routeError(route: string, siteId: string | null, sites: Device[]): string | null {
  const c = parseCidr(route);
  if (!c) return 'Not an IPv4 CIDR, e.g. 192.168.40.0/24.';
  if (c.prefix < 8) return 'Wider than /8.';
  if (!isNetworkAddress(c)) return `Host bits are set: did you mean ${canonicalCidr(c)}?`;
  if (cidrsOverlap(c, parseCidr(OVERLAY_CIDR)!)) return `Overlaps the overlay ${OVERLAY_CIDR}.`;
  for (const [r, what] of RESERVED) {
    if (cidrsOverlap(c, parseCidr(r)!)) return `Overlaps ${r} (${what}).`;
  }
  for (const s of sites) {
    if (s.id === siteId) continue;
    const hit = s.routes.find((r) => {
      const o = parseCidr(r);
      return o !== null && cidrsOverlap(c, o);
    });
    if (hit) return `Overlaps ${hit} on site ${s.name}.`;
  }
  return null;
}

export type RouteState = 'approved' | 'pending' | 'unadvertised';

export interface SiteRoute {
  route: string;
  /** approved = carried and advertised; pending = advertised, not approved; unadvertised = approved, the box no longer reports it. */
  state: RouteState;
}

/** Every route a site advertises or is approved for, sorted as the API sorts routes. */
export function siteRoutes(site: Pick<Device, 'routes' | 'advertisedRoutes'>): SiteRoute[] {
  const all = [...new Set([...site.routes, ...site.advertisedRoutes])].sort(compareCidrs);
  return all.map((route) => ({
    route,
    state: !site.routes.includes(route) ? 'pending' : site.advertisedRoutes.includes(route) ? 'approved' : 'unadvertised',
  }));
}

/** The approved routes plus `route`, as PUT /devices/:id/routes wants them. */
export function withRoute(routes: string[], route: string): string[] {
  return [...new Set([...routes, route])].sort(compareCidrs);
}

export interface Reachability {
  /** The active version; 0 = nothing published, the open policy. */
  version: number;
  /** The sources of the rules below, deduplicated, in policy order ("any", "tag:admin", …). */
  sources: string[];
  /** The enabled allow rules that reach the site, with their 1-based position in the policy. */
  rules: { number: number; rule: PolicyRule }[];
}

/** The endpoints that name a site's LAN: the policy counts its routes wherever the site counts. */
export function siteEndpoints(site: Pick<Device, 'id' | 'network' | 'tags'>): Set<string> {
  return new Set([
    'any',
    `device:${site.id}`,
    ...(site.network ? [`net:${site.network}`] : []),
    ...site.tags.map((t) => `tag:${t}`),
  ]);
}

/** Who the active policy lets reach the hosts behind a site. */
export function reachability(site: Pick<Device, 'id' | 'network' | 'tags'>, policy: PolicyVersion): Reachability {
  if (policy.version === 0) return { version: 0, sources: ['any'], rules: [] };
  const names = siteEndpoints(site);
  const rules = policy.rules
    .map((rule, i) => ({ number: i + 1, rule }))
    .filter(({ rule }) => rule.enabled && rule.action === 'allow' && names.has(rule.destination));
  return { version: policy.version, sources: [...new Set(rules.map((r) => r.rule.source))], rules };
}

function ruleText({ number, rule }: Reachability['rules'][number]): string {
  const service =
    rule.protocol === 'any'
      ? ''
      : `, ${rule.protocol.toUpperCase()}${rule.destinationPorts === 'any' ? '' : ` ${rule.destinationPorts}`}`;
  return `${number} (${rule.source} → ${rule.destination}${service})`;
}

/** The sentence under the hosts table: which rules cover them. */
export function reachFootnote(r: Reachability): string {
  if (r.version === 0) return 'Nothing is published yet: the open policy lets every device reach these hosts.';
  if (r.rules.length === 0) {
    return `No rule of policy v${r.version} lets anything reach these hosts; add one on the Firewall page.`;
  }
  const list = r.rules.map(ruleText);
  const joined = list.length === 1 ? list[0] : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  return `Hosts here are covered by rule${list.length === 1 ? '' : 's'} ${joined} of policy v${r.version}. They need no agent of their own.`;
}

/** The router reports its hosts every 15 min; one missed report is not yet "offline". */
export const REPORT_STALE_MS = 20 * 60_000;

export type SiteStatus = 'online' | 'silent' | 'waiting';

/** waiting = claimed, the router has not collected its token; silent = no host report for 20 min. */
export function siteStatus(site: Pick<Device, 'lastUsedAt' | 'site'>, now: number): SiteStatus {
  const reported = site.site?.reportedAt ? Date.parse(site.site.reportedAt) : Number.NaN;
  if (!Number.isNaN(reported) && now - reported < REPORT_STALE_MS) return 'online';
  return site.lastUsedAt || site.site?.reportedAt ? 'silent' : 'waiting';
}

const CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/**
 * The claim code as the router prints it ("VIP-7K2Q-9ZX4"), from whatever the admin typed:
 * case, spaces, dashes and the VIP prefix are optional. Null until it has the 8 characters.
 */
export function normaliseClaimCode(raw: string): string | null {
  let s = raw.toUpperCase().replace(/[\s-]+/g, '');
  if (s.length === 11 && s.startsWith('VIP')) s = s.slice(3);
  if (s.length !== 8 || [...s].some((ch) => !CODE_ALPHABET.includes(ch))) return null;
  return `VIP-${s.slice(0, 4)}-${s.slice(4)}`;
}

/** The claim drawer's default network: where the existing sites are, else the first one. */
export function defaultSiteNetwork(sites: Device[], networks: { id: string }[]): string | null {
  const used = sites.map((s) => s.network).find((n) => n && networks.some((x) => x.id === n));
  return used ?? networks[0]?.id ?? null;
}
